import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { api } from "@/shared/api-client"
import { useAuthStore } from "./useAuthStore"
import { ForceChange } from "./ForceChange" // jarvis-creds
import { RegisterForm } from "./RegisterPage" // jarvis-register
import { DeviceGate } from "./DeviceGate"

interface LoginResponse {
  access_token: string
  username: string
  role: string
  must_change?: boolean
}

const LOGIN_ERRORS: Record<string, string> = {
  invalid_credentials: "Неверный логин или пароль.",
  too_many_login_attempts: "Слишком много попыток входа. Подождите 15 минут.",
  remote_not_allowed: "Из интернета вход возможен только владельцу. Из дома — по домашней сети.",
  "HTTP 401": "Вход закрыт шлюзом доступа (Cloudflare). Откройте адрес туннеля заново и войдите по коду с почты.",
}

export function LoginPage() {
  const { t } = useTranslation("auth")
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState(() => {
    try {
      if (sessionStorage.getItem("hh-idle-logout")) {
        sessionStorage.removeItem("hh-idle-logout")
        return "Сессия завершена: 15 минут без действий. Войдите снова."
      }
    } catch {
      /* ignore */
    }
    return ""
  })
  const [loading, setLoading] = useState(false)
  const [forced, setForced] = useState<string | null>(null)
  const [mode, setMode] = useState<"login" | "register">("login")
  const [gate, setGate] = useState(false)
  const setAuth = useAuthStore((s) => s.setAuth)
  const navigate = useNavigate()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError("")
    setLoading(true)
    try {
      const res = await api.post<LoginResponse>("/auth/login", { username, password })
      setAuth(res.access_token, res.username, res.role)
      if (res.must_change) {
        setForced(password)
        return
      }
      const me = await api.get<{ device_bound: boolean }>("/registration/me").catch(() => ({ device_bound: false }))
      if (me.device_bound) {
        setGate(true)
        return
      }
      navigate("/")
    } catch (err) {
      const m = err instanceof Error ? err.message : ""
      setError(LOGIN_ERRORS[m] ?? (m || t("login.error")))
    } finally {
      setLoading(false)
    }
  }

  if (gate) {
    return (
      <DeviceGate
        onActive={(a) => {
          setAuth(a.access_token, a.username, a.role)
          navigate("/")
        }}
        onLogout={() => {
          useAuthStore.getState().logout()
          setGate(false)
          setPassword("")
        }}
      />
    )
  }

  if (forced !== null) {
    return (
      <ForceChange
        currentPassword={forced}
        onDone={() => {
          useAuthStore.getState().logout()
          setForced(null)
          setPassword("")
          setError("Данные сохранены. Войдите с новым логином и паролем.")
        }}
      />
    )
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-[#020617] px-4">
      {/* Ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-violet-600/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-indigo-600/8 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm">
        {/* Card */}
        <div className="rounded-2xl border border-white/[8%] bg-white/[3%] backdrop-blur-sm p-8 shadow-2xl shadow-black/40">
          {/* Logo */}
          <div className="text-center mb-8">
            <img
              src="/illustrations/hydra-idle.png"
              alt="HydraHive"
              className="w-24 h-24 mx-auto mb-3 object-contain drop-shadow-[0_0_22px_rgba(34,211,238,0.45)] select-none"
            />
            <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-300 via-violet-300 to-purple-300 bg-clip-text text-transparent">
              HydraHive
            </h1>
            <p className="text-zinc-500 text-sm mt-1">{t("login.subtitle")}</p>
          </div>

          <div className="flex gap-1 mb-6 p-1 rounded-lg bg-white/[4%]">
            <button type="button" onClick={() => setMode("login")}
              className={"flex-1 py-2 text-sm rounded-md transition-colors " + (mode === "login" ? "bg-white/10 text-zinc-100" : "text-zinc-500 hover:text-zinc-300")}>
              Войти
            </button>
            <button type="button" onClick={() => setMode("register")}
              className={"flex-1 py-2 text-sm rounded-md transition-colors " + (mode === "register" ? "bg-white/10 text-zinc-100" : "text-zinc-500 hover:text-zinc-300")}>
              Создать аккаунт
            </button>
          </div>
          {mode === "register" && <RegisterForm onBack={() => setMode("login")} />}
          {mode === "login" && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1.5 uppercase tracking-wide">
                {t("login.username")}
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-lg bg-white/[5%] border border-white/[8%] text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-violet-500/50 focus:border-violet-500/50 transition-colors text-sm"
                placeholder="admin"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1.5 uppercase tracking-wide">
                {t("login.password")}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-lg bg-white/[5%] border border-white/[8%] text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-violet-500/50 focus:border-violet-500/50 transition-colors text-sm"
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <p className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 rounded-lg bg-gradient-to-r from-[var(--hh-accent-from)] to-[var(--hh-accent-to)] hover:brightness-110 text-white font-medium text-sm focus:outline-none focus:ring-2 focus:ring-[var(--hh-accent-border)] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-black/30 mt-2"
            >
              {loading ? t("login.submitting") : t("login.submit")}
            </button>
          </form>
          )}
        </div>
      </div>
    </div>
  )
}
